import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { axiosInstance } from '../services/axios-instance';
import { GiftsService } from '../services/gift-service/service';
import { errorMessage, ObjectUtils, parseAxiosError } from '../utils';
import { Gift, GiftList, GiftStatus } from '../services/gift-service/types';
import { PaginateOptions, StateLoading } from '../services/types';

interface GiftState {
  gifts: Gift[];
  nextPage?: number;
  prevPage?: number;
  currentGift: Gift | null;
  currentPage: number;
  totalPages: number;
  serverError: string;
  loadingGifts: boolean;
  count: number;
  totalCount: number;
  updateGifts: Record<number, StateLoading<Gift>>;
  deleteGiftStatus: StateLoading<number>;
}

interface GiftAction {
  deleteGift: (params: { id: number }) => Promise<void>;
  listGifts: { (options: PaginateOptions): Promise<GiftList> };
  clearCurrentGift: { (): void };
  getDetailedGift: { (id: number): Promise<Gift> };
  updateStatus: (
    gift: Partial<Gift> & { id: number },
    status: GiftStatus,
  ) => Promise<void>;
  updateDetails: (order: Partial<Gift> & { id: number }) => Promise<void>;
}

const initialState = {
  gifts: [] as Gift[],
  nextPage: undefined,
  prevPage: undefined,
  currentGift: null,
  currentPage: 1,
  totalPages: 1,
  count: 0,
  totalCount: 0,
  loadingGifts: false,
  serverError: '',
  updateGifts: {},
  deleteGiftStatus: {},
};

const giftService = new GiftsService(axiosInstance);

export const useGiftStore = create<GiftState & GiftAction>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        deleteGift: async ({ id }) => {
          set({ deleteGiftStatus: { isLoading: true } });
          try {
            await axiosInstance({
              method: 'DELETE',
              url: `/gifts/${id}`,
            });
            set({
              deleteGiftStatus: { isLoading: false },
            });
          } catch (err) {
            set({
              deleteGiftStatus: { isLoading: false, error: errorMessage(err) },
            });
          }
        },
        listGifts: async options => {
          set(() => ({ loadingGifts: true }));
          let data: GiftList;
          try {
            data = await giftService.list(options);
          } catch (error) {
            set(() => ({
              serverError: parseAxiosError(error),
              loadingGifts: false,
            }));
            throw error;
          }
          set(() => ({
            gifts: data.items.map((gift, index) => ({
              ...gift,
              id: index + 1,
              giftId: gift.id,
            })),
            nextPage: data.nextPage,
            prevPage: data.prevPage,
            currentPage: Number(data.page),
            totalPages: Number(data.totalPages),
            count: data.itemCount,
            totalCount: data.totalItems,
            loadingGifts: false,
            serverError: '',
          }));
          return data;
        },
        clearCurrentGift: () => set(() => ({ currentGift: null })),
        getDetailedGift: async id => {
          const gift = await giftService.getById({ id });
          set(() => ({ currentGift: gift }));
          return gift;
        },
        updateStatus: async (gift, status) => {
          const update = (data: StateLoading<Gift>) => {
            set(state => {
              const updateGifts = { ...(state.updateGifts || {}) };
              const cData = { ...updateGifts[gift.id] };
              updateGifts[gift.id] = { ...cData, ...data };
              return { updateGifts };
            });
          };
          update({ isLoading: true });
          try {
            set(state => {
              const gifts = state.gifts || [];
              const index = gifts.findIndex(cGift => cGift.id === gift.id);
              if (index >= 0) {
                const cGift = gifts[index];
                cGift.status = status;
                gifts[index] = cGift;
              }
              return { gifts };
            });

            const currentGift: Gift = gift as Gift;
            currentGift.status = status;
            set({ currentGift });
            update({ isLoading: false, data: currentGift });
          } catch (err) {
            console.log(err);
            update({ isLoading: false, error: errorMessage(err) });
          }
        },
        updateDetails: async gift => {
          const body = ObjectUtils.filterEmpty(gift);
          const update = (data: StateLoading<Gift>) => {
            set(state => {
              const updateGifts = { ...(state.updateGifts || {}) };
              const cData = { ...updateGifts[gift.id] };
              updateGifts[gift.id] = { ...cData, ...data };
              return { updateGifts };
            });
          };
          update({ isLoading: true });
          try {
            set(state => {
              const gifts = state.gifts || [];
              const index = gifts.findIndex(cGift => cGift.id === gift.id);
              if (index >= 0) {
                const cGift = gifts[index];
                gifts[index] = cGift;
              }
              return { gifts };
            });

            const currentGift: Gift = gift as Gift;
            await get().getDetailedGift(Number(gift.id));
            update({ isLoading: false, data: currentGift });
          } catch (err) {
            update({ isLoading: false, error: errorMessage(err) });
            throw err;
          }
        },
      }),
      { name: 'gift-storage' },
    ),
  ),
);
